import ReactDOM from 'react-dom'
import TitleWithSubtitleCell from 'components/table/TitleWithSubtitleCell'
import StatusCell from 'components/table/StatusCell'
import MenuListPopUp from 'components/MenuListPopUp/MenuListPopUp'
import { WppIconChevron } from '@wppopen/components-library-react'
import { CircleFlag } from 'react-circle-flags'
import { createCsApiUrlFromEndpoint, getFormattedDate, getTypeKey } from 'utils'
import { defaultSelectFilterFields } from 'pages/Home/utils'
import { unknown } from 'utils/constants'
import type { ReactNode } from 'react'
import type {
  FilterComponentData,
  SelectFilterComponentData,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import type { ColDef } from 'ag-grid-community'
import type { ImportsListParams } from '@contracts'
import type { ItemImportsListDataV5 } from 'utils/utils.d'
import styles from '../ProjectView.module.scss'

export const RESET_TABLE_POSITION = 0
export const PROJECT_REGION_IDS = getTypeKey<ImportsListParams>('project_region_ids')
export const STATUSES = getTypeKey<ImportsListParams>('statuses')

export const columnStructure = (
  openErrorLogsId: string | undefined,
  isTopPosition: boolean | undefined,
  onDownloadClick: ({ url, file_name }: { url: URL; file_name: string }) => void,
): ColDef<ItemImportsListDataV5>[] => [
  {
    headerName: 'File',
    field: getTypeKey<ItemImportsListDataV5>('file_name'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: ItemImportsListDataV5 }) => {
      const errorCount = data.import_errors?.length
      const renderErrorCounter = errorCount === 1 ? 'error' : 'errors'

      return (
        <TitleWithSubtitleCell
          data-testid="file-name"
          title={data.file_name || unknown}
          subTitle={`${errorCount} ${renderErrorCounter}`}
          subtitleClassName={!!errorCount ? styles.error_counter : undefined}
          icon={
            errorCount ? (
              <WppIconChevron
                direction={
                  openErrorLogsId === data._id && isTopPosition ? 'up' : openErrorLogsId === data._id ? 'down' : 'right'
                }
              />
            ) : undefined
          }
        />
      )
    },
  },
  {
    headerName: 'Region',
    field: getTypeKey<ItemImportsListDataV5>('region_name'),
    sortable: false,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: ItemImportsListDataV5 }) => (
      <span className={styles.flag__container}>
        <CircleFlag countryCode={data.region_flag || ''} height="20" />
        {data.region_name}
      </span>
    ),
  },
  {
    headerName: 'Uploaded',
    field: getTypeKey<ItemImportsListDataV5>('created_at'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: ItemImportsListDataV5 }) => (
      <TitleWithSubtitleCell
        title={getFormattedDate(data.created_at)}
        subTitle={data.update_info?.user_name || unknown}
      />
    ),
  },
  {
    headerName: 'Status',
    field: getTypeKey<ItemImportsListDataV5>('status'),
    sortable: true,
    flex: 1,
    resizable: true,
    cellRenderer: ({ data }: { data: ItemImportsListDataV5 }) => <StatusCell status={data.status} />,
  },

  {
    headerName: '',
    field: 'actions',
    width: 75,
    resizable: true,
    cellRenderer: ({ data }: { data: ItemImportsListDataV5 }) => {
      const url = createCsApiUrlFromEndpoint(`products/imports/${data._id}/download`)

      return (
        <MenuListPopUp
          theme="transparent-border"
          className={styles.table_actions}
          buttonSize="s"
          menuItems={[
            {
              label: 'Download file',
              onClick: () =>
                onDownloadClick({
                  url,
                  file_name: data.file_name || unknown,
                }),
            },
          ]}
        />
      )
    },
  },
]

// PROJECT_IMPORT_LOGS

export const defaultImportLogsRegionFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: PROJECT_REGION_IDS,
  labelText: 'Region',
  isMultiSelect: true,
  options: [{ id: 'all', value: 'All' }],
  defaultValue: { id: 'all', value: 'All' },
}

const defaultImportLogsStatusFilter: SelectFilterComponentData = {
  ...defaultSelectFilterFields,
  id: STATUSES,
  labelText: 'Status',
  isMultiSelect: true,
  options: [
    { id: 'all', value: 'All' },
    { id: 'importing', value: 'Importing' },
    { id: 'failed', value: 'Failed' },
    { id: 'completed', value: 'Completed' },
  ],
  defaultValue: { id: 'all', value: 'All' },
}
export const projectImportLogsFilterData: FilterComponentData[] = [
  defaultImportLogsRegionFilter,
  defaultImportLogsStatusFilter,
]

// Portal component
interface ErrorPortalProps {
  children: ReactNode
  container: Element | undefined
}
// Portal component
export const ErrorPortal = ({ children, container }: ErrorPortalProps) => {
  return container ? ReactDOM.createPortal(children, container) : <></>
}
